import DDLogs from '@/DDLogs';
import { hardRefresh } from '@/router';
import Segment from '@/Segment';
import store from '@/store';
import destination from '@/utils/redesign-destination';

// Read by elsa-v3 on arrival (CashflowRealPage) to track the redirect there: an event fired
// here would race the page unload. Same literals in elsa-v3/src/utils/redesignHandoff.ts.
const REDIRECT_MARKER_KEY = 'redesign_redirect_from';
// elsa-v3's hidden escape hatch (five taps at the bottom of the frame) sends the customer here
// with this session marker: a one-time pass that lets the arrival stay in Vue 2 and tracks the
// opt-out. It is consumed on the first cashflow arrival, before any flag is consulted, so it can
// never linger: v3 also admits the experimental-pages audience, who are not in the redesign flag.
const OPT_OUT_MARKER_KEY = 'redesign_opt_out_from';
// elsa-v3 sends a deep link it can't show (e.g. a savings transaction) back here with this marker:
// a one-time pass, so the arrival isn't redirected straight back. Same literal in redesignHandoff.ts.
const DEEP_LINK_FALLBACK_MARKER_KEY = 'redesign_deep_link_fallback';

// The route the escape came from, or null. Storage can be unavailable (private mode); an
// unreadable marker reads as no opt-out.
function takeMarker(key) {
  try {
    const value = sessionStorage.getItem(key);
    if (value) {
      sessionStorage.removeItem(key);
    }
    return value;
  } catch (error) {
    return null;
  }
}

// Customers in the `redesign-enabled` flag get the elsa-v3 app instead of the Vue 2 cashflow.
// elsa-v3 is a separate SPA served under /v3, so this is a full page load rather than a router
// navigation. Resolves false when the customer stays in Vue 2.
// eslint-disable-next-line import/prefer-default-export
export async function redirectToRedesignIfEnabled(to) {
  const optOutFrom = takeMarker(OPT_OUT_MARKER_KEY);
  const deepLinkFallback = takeMarker(DEEP_LINK_FALLBACK_MARKER_KEY);
  if (optOutFrom) {
    Segment.trackUserInteraction('RedesignOptOut', { from: optOutFrom });
  }
  try {
    await store.dispatch('featureFlags/init');
  } catch (error) {
    DDLogs.error('Failed loading feature flags for the redesign redirect', { error });
    return false;
  }
  if (!store.getters['featureFlags/redesignEnabled']) {
    return false;
  }
  const legacyReason = destination.legacyReasonFor(to, {
    incognito: store.state.session.incognito,
    optedOut: Boolean(optOutFrom),
    deepLinkFallback: Boolean(deepLinkFallback),
  });
  if (legacyReason) {
    DDLogs.log('Keeping a redesign customer in the Vue 2 cashflow', { routeName: to.name, legacyReason });
    return false;
  }
  // Logged without the query: it is caller-controlled and the log PII filter only checks field names.
  const path = destination.redesignPathFor(to.name);
  DDLogs.log('Redirecting to the elsa-v3 redesign', { routeName: to.name, path });
  try {
    sessionStorage.setItem(REDIRECT_MARKER_KEY, to.name);
  } catch (error) {
    // Storage can be unavailable (private mode); the arrival event is best-effort.
  }
  hardRefresh(destination.redesignUrlFor(to.name, to.query));
  return true;
}
